import { engineComponent } from "neo-common";
import { Machine } from "../GameComponents/Machine";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Symbol } from "../GameComponents/Symbol";
import { PlayList } from "../Components/Sounds/PlayList";
import { ModuleManager } from "../Managers/ModuleManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { DataManager } from "../Managers/DataManager";
import { GameData } from "../Channels/Common/Datas/GameData";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Symbols } from "../GameComponents/Symbols";
import { ReelExtended } from "./ReelExtended";
import { CustomUtils } from "../Utils/CustomUtils";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";

/**
 * ReelExtended handles:
 *
 */
@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    private static readonly TeasingDuration: number = 195; //in frames

    protected _fsTeasing: SwitchElement;
    protected _fsTeasingBottom: SwitchElement;
    protected _bonusTeasing: SwitchElement;
    protected _bonusTeasingBottom: SwitchElement;

    protected _canTease2: boolean;

    protected _canTease4: boolean;

    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        fsTeasing?: SwitchElement,
        bonusTeasing?: SwitchElement,
        fsTeasingBottom?: SwitchElement,
        bonusTeasingBottom?: SwitchElement,
    ): void {
        this._fsTeasing = fsTeasing;
        this._fsTeasingBottom = fsTeasingBottom;
        this._bonusTeasing = bonusTeasing;
        this._bonusTeasingBottom = bonusTeasingBottom;

        super.initialize(symbols, reels);
        this.createOverlayReels();
    }

    private createOverlayReels(): void {
        for (let i = 0; i < this._reelCount; i++) {
            let overlayReel = new BaseElement();
            this._reels[i].addOverlayReel(overlayReel);
            this._reelsContainer.addChild(overlayReel);
        }
    }

    public spinStart(
        longRotation?: boolean,
        useTransform: boolean = false,
        currentSymbols?: Array<Array<Symbol>>,
    ): void {
        if (DEBUG) {
            this.log("spinStart");
        }

        ReelExtended.TeaseingSymbols = [];

        this.visible = true;
        this._reelsContainer.visible = true;

        this.actualReelStopped = -1;
        this._stoppedReelIndexes = 0;

        this._stoppedFromServer = false;
        this._stoppedFromMachine = false;
        this._stoppedFromLegal = false;

        this._startSpinTime = Date.now();

        if (this._reels != null) {
            for (let i = 0; i < this._reelCount; i++) {
                if (this._reels[i] != null) {
                    if (this._stoppedSymbolsArray.length > 0) {
                        this._reels[i].visible = true;
                        this._reels[i].setData(this._stoppedSymbolsArray[i]);

                        //move current symbols from WinAnimator to Reel, not array reference
                        this._reels[i].currentSymbols = [];
                        for (let j = 0; j < this._visibleSymbolCount; j++) {
                            this._reels[i].currentSymbols.push(currentSymbols[i][j]);
                            currentSymbols[i][j] = null;
                        }

                        this._reels[i].showSymbols(useTransform);
                        this._reels[i].spinStart();
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no stopped symbols");
                        }
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no reel with index: " + i);
                    }
                }
            }

            if (longRotation != null && longRotation) {
                this._stopTimer.start(this._minSpinRotationLong);
            } else {
                this._stopTimer.start(this._minSpinRotation);
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no reels");
            }
        }

        PlayList.play("SND_BaseRotationStart");
        PlayList.loop("SND_BaseRotation", 0, true);
    }

    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;

            PlayList.stop("SND_BaseFsTeaser");
            PlayList.stop("SND_BaseBonusTeaser");
        }

        if (DEBUG) {
            this.log("spinStop: " + force);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !ModuleManager.freespinsActive() &&
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative")
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            if (force && this._fsTeasing != null) {
                this._fsTeasing.goToCase("EMPTY");
            }
            if (force && this._fsTeasingBottom != null) {
                this._fsTeasingBottom.goToCase("EMPTY");
            }

            if (force && this._bonusTeasing != null) {
                this._bonusTeasing.goToCase("EMPTY");
            }
            if (force && this._bonusTeasingBottom != null) {
                this._bonusTeasingBottom.goToCase("EMPTY");
            }

            this._canTease2 = force ? false : this.checkTeasing(2, CustomUtils.SymbolFs);
            this._canTease4 = force ? false : this.checkTeasing(4, CustomUtils.SymbolBonus);

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            //call spinStop only if not that reel will not be teased or in force stop
                            if (force || (!this._canTease2 && !this._canTease4)) {
                                this._reels[i].spinStop(force);
                            } else {
                                if (this._canTease2 && this._canTease4) {
                                    if (i === 0 || i === 1) {
                                        this._reels[i].spinStop(force);
                                    }
                                } else if (this._canTease2) {
                                    if (i === 0 || i === 1) {
                                        this._reels[i].spinStop(force);
                                    }
                                } else if (this._canTease4) {
                                    if (i === 0 || i === 1 || i === 2 || i === 3) {
                                        this._reels[i].spinStop(force);
                                    }
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    public onReelStopped(reelIndex: number): void {
        this.tease(reelIndex);
        super.onReelStopped(reelIndex);
    }

    protected checkTeasing(reelIndex: number, symbolId: number): boolean {
        let count = 0;
        for (let i = reelIndex - 2; i < reelIndex; i++) {
            if (this._stoppedSymbolsArray[i].symbols.indexOf(symbolId) > -1) {
                count++;
            }
        }

        return count == 2;
    }

    protected tease(reelIndex: number): void {
        switch (reelIndex) {
            case 1:
                if (this._canTease2) {
                    if (this._fsTeasing != null) {
                        this._fsTeasing.goToCase("START");
                    }
                    if (this._fsTeasingBottom != null) {
                        this._fsTeasingBottom.goToCase("START");
                    }

                    PlayList.play("SND_BaseFSTeaser");

                    ReelExtended.TeaseingSymbols = [CustomUtils.SymbolFs];
                    this._reels[2].spinTease([CustomUtils.SymbolFs], MachineExtended.TeasingDuration);
                }
                break;
            case 2:
                if (this._canTease2) {
                    if (this._fsTeasing != null) {
                        this._fsTeasing.goToCase("STOP");
                    }
                    if (this._fsTeasingBottom != null) {
                        this._fsTeasingBottom.goToCase("STOP");
                    }

                    PlayList.stop("SND_BaseFSTeaser", 30);

                    if (this._stoppedSymbolsArray[2].symbols.indexOf(CustomUtils.SymbolFs) == -1) {
                        PlayList.play("SND_BaseFSTeaserFail");
                        ReelExtended.TeaseingSymbols = [];
                    } else {
                        PlayList.play("SND_BaseFSTeaserWin");
                    }

                    this._reels[3].spinStop(false, this._stopDelay);
                }
                break;
            case 3:
                if (this._canTease2 && !this._canTease4) {
                    this._reels[4].spinStop(false, this._stopDelay);
                }

                if (this._canTease4) {
                    if (this._bonusTeasing != null) {
                        this._bonusTeasing.goToCase("START");
                    }
                    if (this._bonusTeasingBottom != null) {
                        this._bonusTeasingBottom.goToCase("START");
                    }

                    PlayList.play("SND_BaseBonusTeaser");

                    ReelExtended.TeaseingSymbols.push(CustomUtils.SymbolBonus);
                    this._reels[4].spinTease([CustomUtils.SymbolBonus], MachineExtended.TeasingDuration);
                }
                break;
            case 4:
                if (this._canTease4 && this._bonusTeasing != null) {
                    this._bonusTeasing.goToCase("STOP");
                    this._bonusTeasingBottom.goToCase("STOP");

                    PlayList.stop("SND_BaseBonusTeaser", 30);

                    if (this._stoppedSymbolsArray[4].symbols.indexOf(CustomUtils.SymbolBonus) == -1) {
                        PlayList.play("SND_BaseBonusTeaserFail");
                        ReelExtended.TeaseingSymbols.pop();
                    } else {
                        PlayList.play("SND_BaseBonusTeaserWin");
                    }
                }
                break;
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/